import api from './api';

// No need for API_BASE since api instance already has baseURL configured
// Just use relative paths like '/api/roles'

// Role API
export interface Role {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
}

export interface RoleCreate {
  name: string;
}

export const getRoles = async (): Promise<Role[]> => {
  const response = await api.get('/api/roles');
  return response.data;
};

export const getRole = async (id: number): Promise<Role> => {
  const response = await api.get(`/api/roles/${id}`);
  return response.data;
};

export const createRole = async (role: RoleCreate): Promise<Role> => {
  const response = await api.post('/api/roles', role);
  return response.data;
};

export const updateRole = async (id: number, role: Partial<RoleCreate>): Promise<Role> => {
  const response = await api.put(`/api/roles/${id}`, role);
  return response.data;
};

export const deleteRole = async (id: number): Promise<void> => {
  await api.delete(`/api/roles/${id}`);
};

// Module API
export interface Module {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
}

export interface ModuleCreate {
  name: string;
}

export const getModules = async (): Promise<Module[]> => {
  const response = await api.get('/api/modules');
  return response.data;
};

export const getModule = async (id: number): Promise<Module> => {
  const response = await api.get(`/api/modules/${id}`);
  return response.data;
};

export const createModule = async (module: ModuleCreate): Promise<Module> => {
  const response = await api.post('/api/modules', module);
  return response.data;
};

export const updateModule = async (id: number, module: Partial<ModuleCreate>): Promise<Module> => {
  const response = await api.put(`/api/modules/${id}`, module);
  return response.data;
};

export const deleteModule = async (id: number): Promise<void> => {
  await api.delete(`/api/modules/${id}`);
};

// Mini Module API
export interface MiniModule {
  id: number;
  name: string;
  module_id: number;
  created_at: string;
  updated_at: string;
  module?: Module;
}

export interface MiniModuleCreate {
  name: string;
  module_id: number;
}

export const getMiniModules = async (moduleId?: number): Promise<MiniModule[]> => {
  const params = moduleId ? { module_id: moduleId } : {};
  const response = await api.get('/api/mini-modules', { params });
  return response.data;
};

export const getMiniModule = async (id: number): Promise<MiniModule> => {
  const response = await api.get(`/api/mini-modules/${id}`);
  return response.data;
};

export const createMiniModule = async (miniModule: MiniModuleCreate): Promise<MiniModule> => {
  const response = await api.post('/api/mini-modules', miniModule);
  return response.data;
};

export const updateMiniModule = async (
  id: number,
  miniModule: Partial<MiniModuleCreate>
): Promise<MiniModule> => {
  const response = await api.put(`/api/mini-modules/${id}`, miniModule);
  return response.data;
};

export const deleteMiniModule = async (id: number): Promise<void> => {
  await api.delete(`/api/mini-modules/${id}`);
};

// Access API
export interface Access {
  id: number;
  code: string;
  created_at: string;
  updated_at: string;
}

export const getAccessCodes = async (): Promise<Access[]> => {
  const response = await api.get('/api/access');
  return response.data;
};

// Profile API
export interface Profile {
  id: number;
  role_id: number;
  module_id: number;
  mini_module_id: number;
  access_id: number;
  created_at: string;
  updated_at: string;
  role?: Role;
  module?: Module;
  mini_module?: MiniModule;
  access?: Access;
}

export interface ProfileCreate {
  role_id: number;
  module_id: number;
  mini_module_id: number;
  access_id: number;
}

export const getProfiles = async (roleId?: number): Promise<Profile[]> => {
  const params = roleId ? { role_id: roleId } : {};
  const response = await api.get('/api/profiles', { params });
  return response.data;
};

export const getProfile = async (id: number): Promise<Profile> => {
  const response = await api.get(`/api/profiles/${id}`);
  return response.data;
};

export const createProfile = async (profile: ProfileCreate): Promise<Profile> => {
  const response = await api.post('/api/profiles', profile);
  return response.data;
};

export const createProfilesBulk = async (profiles: ProfileCreate[]): Promise<Profile[]> => {
  const response = await api.post('/api/profiles/bulk', { profiles });
  return response.data;
};

export const updateProfile = async (
  id: number,
  profile: Partial<ProfileCreate>
): Promise<Profile> => {
  const response = await api.put(`/api/profiles/${id}`, profile);
  return response.data;
};

export const deleteProfile = async (id: number): Promise<void> => {
  await api.delete(`/api/profiles/${id}`);
};

export const deleteProfilesByRole = async (roleId: number): Promise<{deleted_count: number}> => {
  const response = await api.delete(`/api/profiles/role/${roleId}`);
  return response.data;
};

// Helper function to decode access code
export const decodeAccessCode = (code: string) => {
  return {
    view: code[3] === '1',
    add: code[2] === '1',
    edit: code[1] === '1',
    delete: code[0] === '1',
  };
};

// Helper function to get access description
export const getAccessDescription = (code: string): string => {
  const permissions = decodeAccessCode(code);
  const parts = [];

  if (permissions.view) parts.push('Consulter');
  if (permissions.add) parts.push('Ajouter');
  if (permissions.edit) parts.push('Modifier');
  if (permissions.delete) parts.push('Supprimer');

  return parts.length > 0 ? parts.join(', ') : 'Aucun accès';
};
